import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { Toggle } from '../ui/Toggle';
import { ImageField } from '../ui/ImageField';
import { ProductPicker } from '../ui/ProductPicker';
import { ContentEditor } from '../ui/ContentEditor';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NumberInput } from '../ui/NumberInput';
import { CheckList } from '../ui/CheckList';
import { ViewOnSite } from './ViewOnSite';
import { useEditorItem } from './useEditorItem';
import { deleteBlockedReason, routineLinks } from './catalogLinks';
import { useAdmin } from '../AdminContext';
import { SeoPanel } from '../ui/SeoPanel';
import { routineSeoChecks } from './catalogSeo';
import { routineSeoAuto } from '../../utils/seoAuto';
import { nextTierIn, tierIn } from '../../data/gifts';
import { skinTypes } from '../../data/concerns';
import { emptyRoutine, oneOf, routineChecks, routineLevels } from './catalogRules';
import { btnPrimary, inputCls } from '../ui/styles';
import { slugify, uniqueSlug } from '../editing';
import { euro } from '../../core/format/currency';
const back = {
  to: '/be/protocollen',
  label: 'Alle protocollen'
};
const levelLabel: Record<(typeof routineLevels)[number], string> = {
  Start: 'Startprotocol (3–4 producten)',
  Compleet: 'Compleet protocol'
};

/** "Wat erbij zit" komt als lijst uit de ContentEditor; alleen tekstregels tellen. */
const asLines = (value: unknown): string[] => Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** Eén protocol: verhaal, producten in volgorde, wat erbij zit en de prijs (automatisch). */
export function RoutineEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    catalog,
    index,
    site,
    putItem,
    removeItem
  } = useAdmin();
  const {
    isNew,
    item: routine,
    set
  } = useEditorItem('routines', id, emptyRoutine);
  if (!routine) return <MissingItem title="Protocol niet gevonden" back={back} data-id="element-842" data-mp="4rf9z13hjvcz" />;
  const totals = index.routineTotals(routine);
  // De staffel uit het concept: een wijziging in Instellingen is hier meteen te zien.
  const gift = tierIn(site.giftTiers, totals.price);
  const next = nextTierIn(site.giftTiers, totals.price);
  const covered = skinTypes.filter(s => totals.items.some(p => p.skinTypes.includes(s)));
  const usedBy = routineLinks(catalog, routine.id);
  const add = () => {
    const created = {
      ...routine,
      id: uniqueSlug(slugify(routine.name), catalog.routines.map(r => r.id))
    };
    putItem('routines', created);
    navigate(`/be/protocollen/${created.id}`, {
      replace: true
    });
  };
  return <div data-id="element-843" data-mp="8utti1qmutv6">
      <PageHeader title={isNew ? 'Nieuw protocol' : routine.name} back={back} actions={isNew ? <button type="button" onClick={add} disabled={!routine.name.trim()} className={btnPrimary} data-id="element-845" data-mp="11j948c2qeq0p">
              Protocol toevoegen
            </button> : <>
              <DeleteButton undoable blockedReason={deleteBlockedReason(usedBy, 'Kies daar eerst een ander protocol.')} onConfirm={() => {
        const restore = removeItem('routines', routine.id);
        navigate(back.to);
        undoToast(`“${routine.name || 'Naamloos protocol'}” verwijderd`, restore, {
          description: 'Pas definitief als je opslaat.',
          restored: 'Protocol teruggezet'
        });
      }} data-id="element-846" data-mp="1dtqrpeniiy99" />
              <ViewOnSite path="/routines" data-id="element-847" data-mp="21j2vu03zgg4p" />
            </>} data-id="element-844" data-mp="8t0sp32pb3v3" />

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-848" data-mp="26io2yxtb3usi">
        <div className="space-y-6 lg:col-span-8" data-id="element-849" data-mp="26i621nvp8se9">
          <Panel title="Over dit protocol" data-id="element-850" data-mp="2bi0tptnatm0d">
            <div className="grid gap-5 sm:grid-cols-2" data-id="element-851" data-mp="2bjbvjnx6ojou">
              <Field label="Naam" className="sm:col-span-2" data-id="element-852" data-mp="2gglqfj3cdnk2">
                <input value={routine.name} onChange={e => set({
                name: e.target.value
              })} className={inputCls} data-id="element-853" data-mp="2ger6o83t41hk" />
              </Field>
              <Field label="Kernzin" hint="Eén zin op de protocolkaart." className="sm:col-span-2" data-id="element-854" data-mp="389fgh3woici5">
                <input value={routine.claim} onChange={e => set({
                claim: e.target.value
              })} className={inputCls} data-id="element-855" data-mp="3myjwceg044n3" />
              </Field>
              <Field label="Voor wie" className="sm:col-span-2" data-id="element-856" data-mp="3i0a349du7e44">
                <input value={routine.forWho} onChange={e => set({
                forWho: e.target.value
              })} className={inputCls} data-id="element-857" data-mp="2u8uujyvgw0kf" />
              </Field>
              <Field label="Het verhaal" hint="Waarom deze producten samen werken." className="sm:col-span-2" data-id="element-858" data-mp="2pbuohnymaxtb">
                <textarea value={routine.story} onChange={e => set({
                story: e.target.value
              })} rows={4} className={`${inputCls} resize-y leading-relaxed`} data-id="element-859" data-mp="337ydn2umwfyx" />
              </Field>
              <Field label="Soort" data-id="element-860" data-mp="ii8zks1r8y7a">
                <select value={routine.level} onChange={e => {
                const level = oneOf(routineLevels, e.target.value);
                if (level) set({
                  level
                });
              }} className={inputCls} data-id="element-861" data-mp="iloenuvfi78p">
                  {routineLevels.map(l => <option key={l} value={l} data-id="element-862" data-mp="c4mxynag8vdn">
                      {levelLabel[l]}
                    </option>)}
                </select>
              </Field>
              <div className="sm:col-span-2" data-id="element-863" data-mp="14rpkeh1upfnj">
                <Toggle checked={Boolean(routine.featured)} onChange={featured => set({
                featured
              })} label="Uitgelicht op de homepage" description="De eerste twee uitgelichte protocollen staan op de homepage. Een compleet protocol staat ook bovenaan de protocollenpagina." data-id="element-864" data-mp="14t0x56d9xxqs" />
              </div>
              <Field label="Hoort bij behandeling" hint="Wordt aangeboden na die afspraak." data-id="element-865" data-mp="1nqbng1gxhgu3">
                <select value={routine.treatmentSlug ?? ''} onChange={e => set({
                treatmentSlug: e.target.value || undefined
              })} className={inputCls} data-id="element-866" data-mp="1siljlz8mzd4t">
                  <option value="" data-id="element-867" data-mp="1xepik83nmbpn">Geen</option>
                  {catalog.treatments.map(t => <option key={t.slug} value={t.slug} data-id="element-868" data-mp="1td42kdvtphb8">
                      {t.name}
                    </option>)}
                </select>
              </Field>
              <Field label="Gaat minimaal mee (maanden)" data-id="element-869" data-mp="1ogdk5jwjq9mn">
                <NumberInput value={routine.lasts.minMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  minMonths: v
                }
              })} min={1} max={24} data-id="element-870" data-mp="1jfpwn87j0ax6" />
              </Field>
              <Field label="Gaat maximaal mee (maanden)" hint={routine.lasts.maxMonths < routine.lasts.minMonths ? 'Lager dan het minimum: de site toont dan alleen het minimum.' : undefined} data-id="element-871" data-mp="15ocdvwhfzh37">
                <NumberInput value={routine.lasts.maxMonths} onChange={v => v !== undefined && set({
                lasts: {
                  ...routine.lasts,
                  maxMonths: v
                }
              })} min={1} max={24} data-id="element-872" data-mp="vq5bcinwh1kq" />
              </Field>
            </div>
          </Panel>

          <Panel title="Producten, in volgorde van gebruik" description="Reinigen → toner → serum → crème → SPF. Verplaats met de pijltjes." data-id="element-873" data-mp="3h8sab8ig0mg6">
            <ProductPicker value={routine.productIds} onChange={productIds => set({
            productIds
          })} products={catalog.products} ordered data-id="element-874" data-mp="2omr19pvj50r6" />
          </Panel>

          <Panel title="Wat er zonder extra kosten bij zit" description="Nooit een korting — wel begeleiding en papier." data-id="element-875" data-mp="2tj7u2xdoc3al">
            <ContentEditor value={routine.included} onChange={v => set({
            included: asLines(v)
          })} fieldKey="included" data-id="element-876" data-mp="32ddr0sjbbgb4" />
          </Panel>

          <SeoPanel seo={routine.seo} onChange={seo => set({
          seo
        })} auto={routineSeoAuto(routine)} path={null} imageFallback="het sfeerbeeld" data-id="element-877" data-mp="25uyam23xnrmm" />
        </div>

        <aside aria-label="Protocolinstellingen" className="space-y-6 lg:col-span-4" data-id="element-878" data-mp="139qwqnmv53dr">
          <Panel title="Prijs" data-id="element-879" data-mp="yaxigszr58cj">
            <p className="font-display text-4xl font-light text-ink" data-id="element-880" data-mp="ybpg09cwtg5g">{euro(totals.price)}</p>
            <p className="mt-1 text-[13px] text-muted" data-id="element-881" data-mp="1d8pyrnd4i0rs">Som van {totals.items.length} producten, automatisch.</p>
            <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed text-muted" data-id="element-882" data-mp="188266x98xbvg">
              {gift ? <>
                  Klant krijgt: <span className="text-ink" data-id="element-883" data-mp="1h9rys6ev6gut">{gift.title}</span>
                </> : 'Nog geen cadeautrede bereikt.'}
              {next && <>
                  <br data-id="element-884" data-mp="1qzv2b0jm3d7e" />
                  Nog {euro(next.threshold - totals.price)} tot {next.phrase}.
                </>}
            </p>
          </Panel>

          <Panel title="Huidthema’s die dit dekt" data-id="element-885" data-mp="24x9w0hgwe756">
            <p className="text-[13px] leading-relaxed text-muted" data-id="element-886" data-mp="20ysldny9qej0">{covered.length ? covered.join(', ') : 'Nog geen — voeg producten toe.'}</p>
            {usedBy.length > 0 && <p className="mt-3 text-[13px] text-muted" data-id="element-887" data-mp="2olo4ltetanx0">
                Aanbevolen bij: <span className="text-ink" data-id="element-888" data-mp="2ois6a3z1xx81">{usedBy.map(c => c.label).join(', ')}</span>
              </p>}
          </Panel>

          <Panel title="Sfeerbeeld" data-id="element-889" data-mp="2fjolmvk8l9rf">
            <ImageField value={routine.image} onChange={image => set({
            image
          })} fit="cover" aspect="aspect-[4/5]" data-id="element-890" data-mp="2fin01x5lh4vg" />
          </Panel>

          <Panel title="Wat mist er nog?" data-id="element-891" data-mp="25m0fl9h6kfty">
            <CheckList checks={[...routineChecks(routine), ...routineSeoChecks(routine, catalog.routines)]} okText="Dit protocol is compleet" data-id="element-892" data-mp="25n22qcbh0ss1" />
          </Panel>
        </aside>
      </div>
    </div>;
}